import React, { useState, useEffect, useMemo } from 'react';
import { DatePicker, Spin, message, Typography, Row, Col, Card, Radio } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import Header from '../../components/Header/Header';
import SideNavbar from '../../components/Navbar/SideNavbar';
import { DollarOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface CostData {
    name: string;
    label: string;
    count: number;
    total_cost: number;
}

const GENAI_ENDPOINTS = [
    { label: 'Product Chatbot', params: { method: 'handle_chat' } },
    { label: 'Intent Detection', params: { method: 'OrderChatbotIntentDetectionService.detect_intent' } },
    { label: 'Response Gen', params: { method: 'OrderChatbotResponseGenerator.generate_response' } },
    { label: 'Call Analyze', params: { method: 'analyze_ozonetel_call_transcript' } },
    { label: 'Sentiment', params: { method: 'analyze_sentiment' } },
    { label: 'Text Completion', params: { method: 'generate_text_completion' } },
    { label: 'Transcription', params: { method: 'transcribe_ozonetel_recording' } },
];

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#06b6d4'];
const USD_TO_INR = 94.63;

const GenAiCostAnalytics: React.FC = () => {
    const [quickFilter, setQuickFilter] = useState<string>('30days');
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(29, 'day').startOf('day'),
        dayjs().endOf('day')
    ]);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<CostData[]>([]);

    const fetchAllAnalytics = async (start: Dayjs, end: Dayjs) => {
        setLoading(true);
        try {
            const requests = GENAI_ENDPOINTS.map(endpoint =>
                axiosInstance.get(apis.GENAI_COST_ANALYTICS, {
                    params: {
                        ...endpoint.params,
                        start_date: start.toISOString(),
                        end_date: end.toISOString()
                    }
                })
            );

            const responses = await Promise.all(requests);

            const compiledData: CostData[] = responses.map((res, index) => ({
                name: GENAI_ENDPOINTS[index].label,
                label: GENAI_ENDPOINTS[index].label,
                count: res.data?.count || 0,
                total_cost: res.data?.total_cost || 0
            }));

            setData(compiledData);
        } catch (error) {
            console.error('Error fetching GenAI analytics:', error);
            message.error('Error fetching GenAI cost data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            fetchAllAnalytics(dateRange[0], dateRange[1]);
        }
    }, [dateRange]);

    const handleDateChange = (dates: any) => {
        setQuickFilter('');
        if (dates && dates.length === 2) {
            setDateRange([dates[0].startOf('day'), dates[1].endOf('day')]);
        }
    };

    const handleQuickFilter = (e: any) => {
        const filter = e.target.value;
        setQuickFilter(filter);
        const end = dayjs().endOf('day');
        let start = dayjs().startOf('day');

        if (filter === 'today') {
            start = dayjs().startOf('day');
        } else if (filter === 'yesterday') {
            start = dayjs().subtract(1, 'day').startOf('day');
            setDateRange([start, dayjs().subtract(1, 'day').endOf('day')]);
            return;
        } else if (filter === '7days') {
            start = dayjs().subtract(6, 'day').startOf('day');
        } else if (filter === '30days') {
            start = dayjs().subtract(29, 'day').startOf('day');
        } else if (filter === '90days') {
            start = dayjs().subtract(89, 'day').startOf('day');
        }

        setDateRange([start, end]);
    };

    const totalCostSum = useMemo(() => {
        return data.reduce((acc, curr) => acc + curr.total_cost, 0);
    }, [data]);

    const totalUsageCount = useMemo(() => {
        return data.reduce((acc, curr) => acc + curr.count, 0);
    }, [data]);

    const formatINR = (usd: number) => {
        return (usd * USD_TO_INR).toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            style: 'currency',
            currency: 'INR'
        });
    };

    return (
        <React.Fragment>
            <div className="flex mainWrapper w-full">
                <div className="sidenav-wrapper"><SideNavbar /></div>
                <div className="right-container">
                    <div className="header-container"><Header /></div>
                    <div className="body-container">
                        <div style={{ padding: '20px 24px', fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            <div className="bg-white rounded-xl shadow-md p-6 mb-8 flex flex-wrap gap-4 items-center justify-between border border-gray-100">
                                <div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-400 text-white shadow-lg shadow-emerald-200">
                                            <DollarOutlined style={{ fontSize: '20px' }} />
                                        </div>
                                        <div>
                                            <Title level={3} className="!m-0 !font-bold text-gray-800">GenAI Cost Analytics</Title>
                                            <p className="text-gray-400 text-sm mt-0.5">Real-time usage tracking & cost estimation (1 USD ≈ ₹{USD_TO_INR})</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-xl border border-gray-100">
                                    <Radio.Group value={quickFilter} onChange={handleQuickFilter} optionType="button" buttonStyle="solid" className="custom-radio-group">
                                        <Radio.Button value="today">Today</Radio.Button>
                                        <Radio.Button value="yesterday">Yesterday</Radio.Button>
                                        <Radio.Button value="7days">7 Days</Radio.Button>
                                        <Radio.Button value="30days">30 Days</Radio.Button>
                                        <Radio.Button value="90days">90 Days</Radio.Button>
                                    </Radio.Group>
                                    <RangePicker
                                        value={dateRange as any}
                                        onChange={handleDateChange}
                                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                                        className="w-[300px] !rounded-lg border-gray-200"
                                    />
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex flex-col items-center justify-center h-80 bg-white rounded-2xl shadow-sm border border-gray-100">
                                    <Spin size="large" />
                                    <Text className="mt-4 text-gray-400 font-medium">Crunching usage data...</Text>
                                </div>
                            ) : (
                                <Row gutter={[24, 24]}>
                                    <Col span={8}>
                                        <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-indigo-600 via-indigo-700 to-blue-800 text-white overflow-hidden relative">
                                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
                                            <p className="opacity-80 text-sm font-semibold tracking-wide uppercase">Total Cost</p>
                                            <div className="flex items-baseline gap-2 mt-2">
                                                <Title level={2} className="!m-0 !text-white !font-bold">${totalCostSum.toFixed(2)}</Title>
                                                <Text className="text-white/70 font-medium text-lg">({formatINR(totalCostSum)})</Text>
                                            </div>
                                            <Text className="text-white/60 text-xs mt-3 block font-medium">Aggregated across all active models</Text>
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white overflow-hidden relative">
                                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
                                            <p className="opacity-80 text-sm font-semibold tracking-wide uppercase">Total Requests</p>
                                            <Title level={2} className="!m-0 !text-white !font-bold mt-2">{totalUsageCount.toLocaleString()}</Title>
                                            <Text className="text-white/60 text-xs mt-3 block font-medium">Total GenAI API interactions</Text>
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card className="rounded-2xl shadow-lg border-0 bg-gradient-to-br from-amber-500 via-orange-600 to-rose-600 text-white overflow-hidden relative">
                                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
                                            <p className="opacity-80 text-sm font-semibold tracking-wide uppercase">Avg Cost / Request</p>
                                            <div className="flex items-baseline gap-2 mt-2">
                                                <Title level={2} className="!m-0 !text-white !font-bold">
                                                    ${totalUsageCount > 0 ? (totalCostSum / totalUsageCount).toFixed(4) : '0.0000'}
                                                </Title>
                                                <Text className="text-white/70 font-medium text-sm">
                                                    ({formatINR(totalUsageCount > 0 ? totalCostSum / totalUsageCount : 0)})
                                                </Text>
                                            </div>
                                            <Text className="text-white/60 text-xs mt-3 block font-medium">Unit cost efficiency</Text>
                                        </Card>
                                    </Col>

                                    <Col span={12}>
                                        <Card
                                            title={<Text className="text-gray-700 font-bold text-lg">Cost Distribution by Service</Text>}
                                            className="rounded-2xl shadow-sm border border-gray-100"
                                        >
                                            <div style={{ width: '100%', height: 380 }}>
                                                <ResponsiveContainer>
                                                    <BarChart
                                                        data={data}
                                                        margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                                                    >
                                                        <defs>
                                                            {COLORS.map((color, index) => (
                                                                <linearGradient key={`grad-${index}`} id={`colorGrad-${index}`} x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor={color} stopOpacity={0.9} />
                                                                    <stop offset="95%" stopColor={color} stopOpacity={0.6} />
                                                                </linearGradient>
                                                            ))}
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                        <XAxis
                                                            dataKey="name"
                                                            axisLine={false}
                                                            tickLine={false}
                                                            angle={-45}
                                                            textAnchor="end"
                                                            height={60}
                                                            tick={{ fill: '#64748b', fontSize: 10, fontWeight: 500 }}
                                                        />
                                                        <YAxis
                                                            axisLine={false}
                                                            tickLine={false}
                                                            tick={{ fill: '#64748b', fontSize: 11 }}
                                                        />
                                                        <RechartsTooltip
                                                            cursor={{ fill: '#f8fafc' }}
                                                            contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', padding: '12px' }}
                                                            formatter={(value: any, name: any) => {
                                                                if (name === 'Cost (USD)') {
                                                                    const usd = value || 0;
                                                                    const inr = usd * USD_TO_INR;
                                                                    return [`$${usd.toFixed(4)} (₹${inr.toFixed(2)})`, 'Estimated Cost'];
                                                                }
                                                                return [value, name];
                                                            }}
                                                        />
                                                        <Bar dataKey="total_cost" name="Cost (USD)" radius={[6, 6, 0, 0]} barSize={35}>
                                                            {data.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={`url(#colorGrad-${index % COLORS.length})`} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </Card>
                                    </Col>

                                    <Col span={12}>
                                        <Card
                                            title={<Text className="text-gray-700 font-bold text-lg">Hit Distribution (Volume)</Text>}
                                            className="rounded-2xl shadow-sm border border-gray-100"
                                        >
                                            <div style={{ width: '100%', height: 380 }}>
                                                <ResponsiveContainer>
                                                    <BarChart
                                                        data={data}
                                                        margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                        <XAxis
                                                            dataKey="name"
                                                            axisLine={false}
                                                            tickLine={false}
                                                            angle={-45}
                                                            textAnchor="end"
                                                            height={60}
                                                            tick={{ fill: '#64748b', fontSize: 10, fontWeight: 500 }}
                                                        />
                                                        <YAxis
                                                            axisLine={false}
                                                            tickLine={false}
                                                            tick={{ fill: '#64748b', fontSize: 11 }}
                                                        />
                                                        <RechartsTooltip
                                                            cursor={{ fill: '#f8fafc' }}
                                                            contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', padding: '12px' }}
                                                        />
                                                        <Bar dataKey="count" name="API Requests" radius={[6, 6, 0, 0]} barSize={35}>
                                                            {data.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[(index + 3) % COLORS.length]} fillOpacity={0.8} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </Card>
                                    </Col>

                                </Row>
                            )}
                        </div>
                        <style>{`
                .custom-radio-group .ant-radio-button-wrapper {
                    border-radius: 8px !important;
                    border: none !important;
                    margin: 0 4px !important;
                    background: transparent !important;
                    color: #64748b !important;
                    font-weight: 500 !important;
                }
                .custom-radio-group .ant-radio-button-wrapper-checked {
                    background: #fff !important;
                    color: #4f46e5 !important;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
                }
                .custom-radio-group .ant-radio-button-wrapper:before {
                    display: none !important;
                }
            `}</style>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default GenAiCostAnalytics;
